import { dao } from '@platform/dao';
import type { IAssignment, IAssignmentWrite, IAttachment, IComment, ICommentWrite, IHistoryEntry, ILink, ILinkWrite } from '@platform/core/types';
import { baseApi, run } from './baseApi';
import { linkTags } from './linkTags';

export const collabApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    comments: b.query<IComment[], string>({ queryFn: (id) => run(() => dao.collaboration.comments(id)), providesTags: (_r, _e, id) => [{ type: 'Comments', id }] }),
    addComment: b.mutation<IComment, { recordId: string; body: ICommentWrite }>({
      queryFn: ({ recordId, body }) => run(() => dao.collaboration.addComment(recordId, body)),
      invalidatesTags: (_r, _e, { recordId }) => [{ type: 'Comments', id: recordId }, { type: 'History', id: recordId }],
    }),
    files: b.query<IAttachment[], string>({ queryFn: (id) => run(() => dao.collaboration.files(id)), providesTags: (_r, _e, id) => [{ type: 'Files', id }] }),
    uploadFile: b.mutation<IAttachment, { recordId: string; file: File }>({
      queryFn: ({ recordId, file }) => run(() => dao.collaboration.upload(recordId, file)),
      invalidatesTags: (_r, _e, { recordId }) => [{ type: 'Files', id: recordId }, { type: 'History', id: recordId }],
    }),
    assignments: b.query<IAssignment[], string>({ queryFn: (id) => run(() => dao.collaboration.assignments(id)), providesTags: (_r, _e, id) => [{ type: 'Assignments', id }] }),
    addAssignment: b.mutation<IAssignment, { recordId: string; body: IAssignmentWrite }>({
      queryFn: ({ recordId, body }) => run(() => dao.collaboration.addAssignment(recordId, body)),
      invalidatesTags: (_r, _e, { recordId }) => [{ type: 'Assignments', id: recordId }, { type: 'History', id: recordId }, 'Inbox'],
    }),
    history: b.query<IHistoryEntry[], { recordId: string; page?: number }>({
      queryFn: ({ recordId, page }) => run(() => dao.collaboration.history(recordId, page ?? 1)),
      providesTags: (_r, _e, { recordId }) => [{ type: 'History', id: recordId }],
    }),
    watch: b.mutation<{ watching: boolean }, { recordId: string; on: boolean }>({
      queryFn: ({ recordId, on }) => run(() => dao.collaboration.watch(recordId, on)),
    }),
    links: b.query<ILink[], string>({ queryFn: (id) => run(() => dao.collaboration.links(id)), providesTags: (_r, _e, id) => [{ type: 'Links', id }] }),
    /** Додавання зв'язку оновлює зв'язки й історію обох записів і зв'язки типів. */
    addLink: b.mutation<ILink, { recordId: string; body: ILinkWrite }>({
      queryFn: ({ recordId, body }) => run(() => dao.collaboration.addLink(recordId, body)),
      invalidatesTags: (_r, _e, { recordId, body }) => linkTags(recordId, body.toRecordId),
    }),
    /** Видалення зв'язку: `otherId` — інший кінець (його зв'язки й історія теж змінюються); без нього оновлюються лише запис і зв'язки типів. */
    removeLink: b.mutation<void, { recordId: string; linkId: string; otherId?: string }>({
      queryFn: ({ recordId, linkId }) => run(() => dao.collaboration.removeLink(recordId, linkId)),
      invalidatesTags: (_r, _e, { recordId, otherId }) => linkTags(recordId, otherId),
    }),
  }),
});

export const {
  useCommentsQuery, useAddCommentMutation, useFilesQuery, useUploadFileMutation, useAssignmentsQuery, useAddAssignmentMutation,
  useHistoryQuery, useWatchMutation, useLinksQuery, useAddLinkMutation, useRemoveLinkMutation,
} = collabApi;
